<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import { sanitizeAllowedDomains } from 'dashboard/helper/URLHelper';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  CUSTOM_LAUNCHER_STYLE_ID,
  WEB_WIDGET_LAUNCHER_STYLES,
  bubbleStyleToType,
} from 'dashboard/constants/webWidgetLauncherStyles';
import {
  INBOX_AVATAR_ACCEPT,
  validateInboxAvatarFile,
} from 'dashboard/helper/inboxAvatarUpload';

const BUBBLE_STYLES = WEB_WIDGET_LAUNCHER_STYLES;

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const websiteDomains = ref([]);
const isUrlInputFocused = ref(false);
const channelWidgetColor = ref('#009CE0');
const bubbleIconStyle = ref('1');
const customIconFile = ref(null);
const customIconPreview = ref(null);
const iconFileInputRef = ref(null);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const canSubmit = computed(() => websiteDomains.value.length > 0);

const normalizeUrl = raw => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const isValidUrl = url => {
  try {
    const normalized = normalizeUrl(url);
    if (!/^https?:\/\//i.test(normalized)) return false;
    // eslint-disable-next-line no-new
    new URL(normalized);
    return true;
  } catch {
    return false;
  }
};

const urlToDomain = url => {
  try {
    return new URL(normalizeUrl(url)).hostname.replace(/^www\./i, '');
  } catch {
    return url;
  }
};

const deriveInboxName = url => {
  const domain = urlToDomain(url);
  return domain
    .split('.')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const websiteUrls = computed(() =>
  websiteDomains.value.map(domain => normalizeUrl(domain))
);

const normalizeDomainTag = raw => urlToDomain(normalizeUrl(raw));

const onWebsiteDomainAdd = ({ value }) => {
  const domain = normalizeDomainTag(value);
  if (!domain || !isValidUrl(normalizeUrl(value))) {
    websiteDomains.value = websiteDomains.value.filter(tag => tag !== value);
    return;
  }
  const withoutDuplicates = websiteDomains.value.filter(
    tag => tag !== value && normalizeDomainTag(tag) !== domain
  );
  websiteDomains.value = [...withoutDuplicates, domain];
};

const selectBubbleStyle = styleId => {
  bubbleIconStyle.value = styleId;
};

const selectCustomBubbleStyle = () => {
  if (!customIconPreview.value) return;
  bubbleIconStyle.value = CUSTOM_LAUNCHER_STYLE_ID;
};

const onCustomIconChange = event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  const validation = validateInboxAvatarFile(file);
  if (!validation.valid) {
    useAlert(
      t(
        `INBOX_MGMT.WIDGET_CUSTOMIZATION_SECTIONS.AVATAR_UPLOAD.${validation.errorKey}`
      )
    );
    return;
  }

  if (customIconPreview.value) {
    URL.revokeObjectURL(customIconPreview.value);
  }
  customIconFile.value = file;
  customIconPreview.value = URL.createObjectURL(file);
  bubbleIconStyle.value = CUSTOM_LAUNCHER_STYLE_ID;
};

const openCustomIconPicker = () => {
  iconFileInputRef.value?.click();
};

const saveWidgetBubbleSettings = inboxId => {
  if (!inboxId) return;

  LocalStorage.set(`${LOCAL_STORAGE_KEYS.WIDGET_BUILDER}${inboxId}`, {
    position: 'right',
    type: bubbleStyleToType(bubbleIconStyle.value),
    launcherTitle: '',
    launcherIconStyle: bubbleIconStyle.value,
    widgetSize: 'medium',
    displayOption: 'mobile_and_desktop',
    verticalSpacing: 'standard',
    horizontalSpacing: 'standard',
  });
};

const createChannel = async () => {
  if (!websiteDomains.value.length) return;

  const primaryUrl = websiteUrls.value[0];
  const allowedDomains = sanitizeAllowedDomains(websiteDomains.value.join(','));

  try {
    const website = await store.dispatch('inboxes/createWebsiteChannel', {
      name: deriveInboxName(primaryUrl),
      ...(bubbleIconStyle.value === CUSTOM_LAUNCHER_STYLE_ID &&
      customIconFile.value
        ? { avatar: customIconFile.value }
        : {}),
      channel: {
        type: 'web_widget',
        website_url: primaryUrl,
        allowed_domains: allowedDomains,
        widget_color: channelWidgetColor.value,
      },
    });

    saveWidgetBubbleSettings(website.id);

    router.replace({
      name: 'settings_inbox_finish',
      params: {
        accountId: route.params.accountId,
        inbox_id: website.id,
      },
    });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.DESC')"
    />
    <woot-loading-state
      v-if="uiFlags.isCreating"
      :message="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.LOADING_MESSAGE')"
    />
    <form
      v-if="!uiFlags.isCreating"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel"
    >
      <div class="w-full">
        <label class="mb-2 block text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WEBSITE_URLS.LABEL') }}
        </label>
        <div
          class="flex min-h-11 items-start gap-2 rounded-xl border bg-n-alpha-white2 px-3 py-2 transition-colors"
          :class="isUrlInputFocused ? 'border-n-brand' : 'border-n-weak'"
        >
          <TagInput
            v-model="websiteDomains"
            class="min-w-0 flex-1"
            allow-create
            :auto-open-dropdown="false"
            :placeholder="
              websiteDomains.length
                ? ''
                : $t('INBOX_MGMT.ADD.WEBSITE_URLS.PLACEHOLDER')
            "
            @add="onWebsiteDomainAdd"
            @focus="isUrlInputFocused = true"
            @on-click-outside="isUrlInputFocused = false"
          />
          <Icon
            icon="i-lucide-chevron-down"
            class="mt-1.5 size-4 shrink-0 text-n-slate-11"
          />
        </div>
      </div>

      <div class="w-full mt-4">
        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WIDGET_STYLE.LABEL') }}
        </p>
        <div class="flex flex-wrap items-end gap-3">
          <button
            v-for="style in BUBBLE_STYLES"
            :key="style.id"
            type="button"
            class="flex shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200"
            :class="
              bubbleIconStyle === style.id
                ? 'size-14 border-n-brand bg-n-brand text-white'
                : 'size-12 border-transparent bg-n-brand text-white opacity-80 hover:opacity-100'
            "
            @click="selectBubbleStyle(style.id)"
          >
            <Icon
              :icon="style.icon"
              :class="bubbleIconStyle === style.id ? 'size-6' : 'size-5'"
            />
          </button>
          <button
            v-if="customIconPreview"
            type="button"
            class="flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 transition-all duration-200"
            :class="
              bubbleIconStyle === CUSTOM_LAUNCHER_STYLE_ID
                ? 'size-14 border-n-brand'
                : 'size-12 border-transparent opacity-80 hover:opacity-100'
            "
            :style="{ backgroundColor: channelWidgetColor }"
            @click="selectCustomBubbleStyle"
          >
            <img
              :src="customIconPreview"
              alt=""
              class="shrink-0 rounded-full object-cover object-center"
              :class="
                bubbleIconStyle === CUSTOM_LAUNCHER_STYLE_ID
                  ? 'size-8'
                  : 'size-6'
              "
            />
          </button>
          <button
            type="button"
            class="flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-dashed transition-colors"
            :class="
              bubbleIconStyle === CUSTOM_LAUNCHER_STYLE_ID && customIconPreview
                ? 'border-n-brand bg-n-alpha-2 text-n-brand'
                : 'border-n-weak bg-n-alpha-2 text-n-slate-11 hover:border-n-brand hover:text-n-brand'
            "
            :aria-label="$t('INBOX_MGMT.ADD.WIDGET_STYLE.CUSTOM_ICON_LABEL')"
            @click="openCustomIconPicker"
          >
            <Icon icon="i-lucide-plus" class="size-5" />
          </button>
          <input
            ref="iconFileInputRef"
            type="file"
            :accept="INBOX_AVATAR_ACCEPT"
            class="hidden"
            @change="onCustomIconChange"
          />
        </div>
      </div>

      <div class="w-full mt-4">
        <label>
          {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL') }}
          <woot-color-picker v-model="channelWidgetColor" />
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-4">
        <div class="w-full">
          <NextButton
            type="submit"
            :is-loading="uiFlags.isCreating"
            :disabled="!canSubmit"
            variant="solid"
            color="blue"
            :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SUBMIT_BUTTON')"
          />
        </div>
      </div>
    </form>
  </div>
</template>
